/**
 * FiscalDashboardView — Fleet Fiscal Intelligence Dashboard (ex-TVS)
 *
 * 3 tabs:
 * 1. Fiscalité CO2 (Block A) — fleet tax breakdown, top 10, fuel distribution
 * 2. Conformité ZFE (Block C) — zone compliance matrix
 * 3. Stratégie Verdissement (Block B) — low-emission ratio, replacement priority
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Receipt, Leaf, TrendingUp, ShieldCheck, ShieldX, ShieldAlert, HelpCircle,
  RefreshCw, Loader2, Zap, AlertTriangle, Car, Search
} from 'lucide-react';
import { tvsAPI } from '../../api/index.js';
import ReplacementSimulatorModal from './ReplacementSimulatorModal.jsx';
import { useOrgCurrency } from '../../hooks/useOrgCurrency.js';


const ZFE_ICON = {
  allowed: { Icon: ShieldCheck, color: 'text-green-600', bg: 'bg-green-50' },
  restricted: { Icon: ShieldAlert, color: 'text-amber-600', bg: 'bg-amber-50' },
  prohibited: { Icon: ShieldX, color: 'text-red-600', bg: 'bg-red-50' },
  unknown: { Icon: HelpCircle, color: 'text-gray-400', bg: 'bg-gray-50' },
};

function FiscalDashboardView({ onMessage }) {
  const { t } = useTranslation();
  // TVS : agregat fiscal du perimetre, rendu dans sa devise (jamais EUR en dur).
  const { moneyScope } = useOrgCurrency();
  const formatEur = (n) => (n != null ? moneyScope(n, { decimals: 0 }) : '—');
  const [activeTab, setActiveTab] = useState('fiscal');
  const [selectedYear, setSelectedYear] = useState(2026);
  const [fleetTax, setFleetTax] = useState(null);
  const [fleetTax2025, setFleetTax2025] = useState(null);
  const [fleetTax2027, setFleetTax2027] = useState(null);
  const [zfeAll, setZfeAll] = useState(null);
  const [loading, setLoading] = useState(true);
  const [simulateVehicle, setSimulateVehicle] = useState(null);
  const [search, setSearch] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [taxRes, tax25Res, tax27Res, zfeRes] = await Promise.all([
        tvsAPI.getFleetTax(selectedYear),
        tvsAPI.getFleetTax(2025),
        tvsAPI.getFleetTax(2027),
        tvsAPI.getFleetAllZones(),
      ]);
      setFleetTax(taxRes.data);
      setFleetTax2025(tax25Res.data);
      setFleetTax2027(tax27Res.data);
      setZfeAll(zfeRes.data);
    } catch (err) {
      onMessage?.({ title: t('common.messages.errorTitle'), message: t('fiscal.messages.loadError'), type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [selectedYear, onMessage]);

  useEffect(() => { loadData(); }, [loadData]);

  const tabs = [
    { id: 'fiscal', label: t('fiscal.tabs.co2'), icon: Receipt },
    { id: 'zfe', label: t('fiscal.tabs.zfe'), icon: Leaf },
    { id: 'green', label: t('fiscal.tabs.green'), icon: TrendingUp },
  ];

  // Filter vehicles
  const filteredDetails = fleetTax?.details?.filter(d => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (d.license_plate || '').toLowerCase().includes(q)
      || (d.make || '').toLowerCase().includes(q)
      || (d.model || '').toLowerCase().includes(q);
  }) || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Receipt className="w-6 h-6 text-blue-700" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{t('fiscal.header.title')}</h1>
              <p className="text-sm text-gray-500">{t('fiscal.header.subtitle')}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={selectedYear}
              onChange={e => setSelectedYear(parseInt(e.target.value))}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value={2025}>2025</option>
              <option value={2026}>2026</option>
              <option value={2027}>2027</option>
            </select>
            <button onClick={loadData} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <RefreshCw className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mt-4 border-b border-gray-200">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition ${
                activeTab === id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500"><Loader2 className="w-8 h-8 animate-spin mx-auto" /></div>
      ) : (
        <>
          {/* ═══ TAB 1: Fiscalité CO2 ═══ */}
          {activeTab === 'fiscal' && fleetTax && (
            <div className="space-y-4">
              {/* KPI cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="bg-white rounded-lg shadow-sm p-4 text-center border-l-4 border-blue-500">
                  <p className="text-xs text-gray-500 uppercase">{t('fiscal.kpi.totalExTvs')}</p>
                  <p className="text-2xl font-bold text-blue-700">{formatEur(fleetTax.total_fleet_tax)}</p>
                </div>
                <div className="bg-white rounded-lg shadow-sm p-4 text-center">
                  <p className="text-xs text-gray-500 uppercase">{t('fiscal.kpi.co2Tax')}</p>
                  <p className="text-lg font-bold text-gray-900">{formatEur(fleetTax.total_co2_tax)}</p>
                </div>
                <div className="bg-white rounded-lg shadow-sm p-4 text-center">
                  <p className="text-xs text-gray-500 uppercase">{t('fiscal.kpi.pollutantTax')}</p>
                  <p className="text-lg font-bold text-gray-900">{formatEur(fleetTax.total_pollutant_tax)}</p>
                </div>
                <div className="bg-white rounded-lg shadow-sm p-4 text-center">
                  <p className="text-xs text-gray-500 uppercase">{t('fiscal.kpi.taxableVehicles')}</p>
                  <p className="text-lg font-bold text-gray-900">{fleetTax.taxable_count} <span className="text-sm text-gray-500">/ {fleetTax.vehicle_count}</span></p>
                </div>
                <div className="bg-white rounded-lg shadow-sm p-4 text-center">
                  <p className="text-xs text-gray-500 uppercase">{t('fiscal.kpi.averageCost')}</p>
                  <p className="text-lg font-bold text-gray-900">{formatEur(fleetTax.average_per_taxable_vehicle)}</p>
                </div>
              </div>

              {/* 3-year evolution */}
              <div className="bg-white rounded-lg shadow-sm p-4">
                <p className="text-sm font-semibold text-gray-700 mb-3">{t('fiscal.evolution.title')}</p>
                <div className="grid grid-cols-3 gap-4 text-center">
                  {[
                    { year: 2025, data: fleetTax2025 },
                    { year: selectedYear, data: fleetTax },
                    { year: 2027, data: fleetTax2027 },
                  ].map(({ year, data }) => (
                    <div key={year} className={`p-3 rounded-lg ${year === selectedYear ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50'}`}>
                      <p className="text-xs text-gray-500 uppercase">{year}</p>
                      <p className={`text-xl font-bold ${year === selectedYear ? 'text-blue-700' : 'text-gray-700'}`}>
                        {formatEur(data?.total_fleet_tax || 0)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Vehicle table */}
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="p-3 border-b border-gray-200 flex items-center gap-2">
                  <Search className="w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder={t('fiscal.searchPlaceholder')}
                    className="flex-1 text-sm border-none focus:ring-0 outline-none"
                  />
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('fiscal.table.plate')}</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('fiscal.table.vehicle')}</th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">{t('fiscal.table.status')}</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('fiscal.table.co2')}</th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">{t('fiscal.table.critAir')}</th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">{t('fiscal.table.scale')}</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('fiscal.table.co2Tax')}</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('fiscal.table.pollutants')}</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('fiscal.table.total')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredDetails.sort((a, b) => (b.total_prorated || 0) - (a.total_prorated || 0)).slice(0, 50).map(d => (
                        <tr key={d.vehicle_id} className="hover:bg-blue-50 cursor-pointer" onClick={() => setSimulateVehicle(d)}>
                          <td className="px-3 py-2 font-mono text-xs">{d.license_plate}</td>
                          <td className="px-3 py-2">{d.make} {d.model}</td>
                          <td className="px-3 py-2 text-center">
                            <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                              d.exempt ? 'bg-green-100 text-green-800' :
                              d.fiscal_scope === 'outside' ? 'bg-gray-100 text-gray-600' :
                              d.fiscal_scope === 'review' ? 'bg-amber-100 text-amber-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {d.exempt ? t('fiscal.status.exempt') : d.fiscal_scope === 'outside' ? t('fiscal.status.outsideScope') : d.fiscal_scope === 'review' ? t('fiscal.status.review') : t('fiscal.status.taxable')}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right">{d.co2_gkm || '—'}</td>
                          <td className="px-3 py-2 text-center text-xs font-medium">{d.crit_air || '—'}</td>
                          <td className="px-3 py-2 text-center text-xs">{d.bareme_type?.toUpperCase() || '—'}</td>
                          <td className="px-3 py-2 text-right">{formatEur(d.co2_tax_net)}</td>
                          <td className="px-3 py-2 text-right">{formatEur(d.pollutant_tax_net)}</td>
                          <td className="px-3 py-2 text-right font-semibold">{formatEur(d.total_prorated)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ═══ TAB 2: Conformité ZFE ═══ */}
          {activeTab === 'zfe' && zfeAll && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {zfeAll.map(zone => {
                  const total = zone.allowed + zone.restricted + zone.prohibited + zone.unknown;
                  const pctOk = total > 0 ? Math.round((zone.allowed / total) * 100) : 0;
                  return (
                    <div key={zone.zone_code} className="bg-white rounded-lg shadow-sm p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-gray-900 text-sm">{zone.zone_name}</h3>
                        <span className={`text-xs font-bold ${pctOk >= 80 ? 'text-green-600' : pctOk >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                          {pctOk}%
                        </span>
                      </div>
                      {/* Compliance bar */}
                      <div className="flex h-3 rounded-full overflow-hidden bg-gray-100">
                        {zone.allowed > 0 && <div className="bg-green-500" style={{ width: `${(zone.allowed / total) * 100}%` }} />}
                        {zone.restricted > 0 && <div className="bg-amber-400" style={{ width: `${(zone.restricted / total) * 100}%` }} />}
                        {zone.prohibited > 0 && <div className="bg-red-500" style={{ width: `${(zone.prohibited / total) * 100}%` }} />}
                        {zone.unknown > 0 && <div className="bg-gray-300" style={{ width: `${(zone.unknown / total) * 100}%` }} />}
                      </div>
                      <div className="flex justify-between mt-2 text-xs text-gray-500">
                        <span className="text-green-600">{t('fiscal.zfe.allowed', { count: zone.allowed })}</span>
                        {zone.prohibited > 0 && <span className="text-red-600">{t('fiscal.zfe.prohibited', { count: zone.prohibited })}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ═══ TAB 3: Stratégie Verdissement ═══ */}
          {activeTab === 'green' && fleetTax && (
            <div className="space-y-4">
              {/* Low-emission ratio gauge */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{t('fiscal.green.ratioTitle')}</h3>
                    <p className="text-sm text-gray-500">{t('fiscal.green.legalTarget', { year: selectedYear, pct: Math.round((fleetTax.low_emission_target || 0.15) * 100) })}</p>
                  </div>
                  <div className={`text-3xl font-bold ${fleetTax.low_emission_compliant ? 'text-green-600' : 'text-red-600'}`}>
                    {Math.round((fleetTax.low_emission_ratio || 0) * 100)}%
                  </div>
                </div>
                {/* Gauge bar */}
                <div className="relative h-6 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${fleetTax.low_emission_compliant ? 'bg-green-500' : 'bg-red-500'}`}
                    style={{ width: `${Math.min(100, (fleetTax.low_emission_ratio || 0) * 100)}%` }}
                  />
                  {/* Target marker */}
                  <div
                    className="absolute top-0 h-full w-0.5 bg-gray-800"
                    style={{ left: `${(fleetTax.low_emission_target || 0.15) * 100}%` }}
                    title={t('fiscal.green.targetTooltip', { pct: Math.round((fleetTax.low_emission_target || 0.15) * 100) })}
                  />
                </div>
                <div className="flex justify-between mt-1 text-xs text-gray-500">
                  <span>{t('fiscal.green.countSummary', { lowEmissionCount: fleetTax.low_emission_count, total: fleetTax.vehicle_count })}</span>
                  <span>{fleetTax.low_emission_compliant ? t('fiscal.green.compliant') : t('fiscal.green.nonCompliant')}</span>
                </div>
                <p className="text-xs text-gray-400 mt-2 italic">
                  {t('fiscal.green.incentiveTaxHint')}
                </p>
              </div>

              {/* Top 10 to replace */}
              <div className="bg-white rounded-lg shadow-sm p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  {t('fiscal.green.top10Title')}
                </h3>
                <div className="space-y-2">
                  {(fleetTax.details || [])
                    .filter(d => !d.exempt && d.fiscal_scope === 'taxable')
                    .sort((a, b) => (b.total_prorated || 0) - (a.total_prorated || 0))
                    .slice(0, 10)
                    .map((d, i) => (
                      <div
                        key={d.vehicle_id}
                        className="flex items-center justify-between p-2 bg-gray-50 rounded hover:bg-blue-50 cursor-pointer transition"
                        onClick={() => setSimulateVehicle(d)}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-xs font-bold text-gray-400 w-5">#{i + 1}</span>
                          <Car className="w-4 h-4 text-gray-400" />
                          <div>
                            <span className="text-sm font-medium">{d.license_plate}</span>
                            <span className="text-xs text-gray-500 ml-2">{d.make} {d.model}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-gray-500">{d.co2_gkm} g/km</span>
                          <span className="text-sm font-bold text-red-700">{formatEur(d.total_prorated)}</span>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Replacement simulator modal */}
      {simulateVehicle && (
        <ReplacementSimulatorModal
          isOpen={!!simulateVehicle}
          onClose={() => setSimulateVehicle(null)}
          vehicle={simulateVehicle}
        />
      )}
    </div>
  );
}

export default FiscalDashboardView;
